import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Stack,
} from "@mui/material";
import useWaterUnit from "../../../../../hooks/useWaterUnit";
import LocalDrink from "@mui/icons-material/LocalDrink";
import Grass from "@mui/icons-material/Grass";
import Restaurant from "@mui/icons-material/Restaurant";
const Stat = ({ icon: Icon, label, value, sub }) => (
  <Grid item xs={6} sm={4}>
    <Stack direction="row" spacing={1} alignItems="flex-start">
      {Icon ? <Icon color="secondary" sx={{ fontSize: 28, mt: 0.25 }} /> : null}
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {value}
        </Typography>
        {sub ? (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  </Grid>
);

/**
 * Latest day in nutrition series vs active nutrition goal (if any).
 */
const NutritionTrackingSnapshotCard = ({ client, nutritionChartData }) => {
  const { waterLabel } = useWaterUnit();
  const latest = React.useMemo(() => {
    if (!nutritionChartData || !nutritionChartData.length) return null;
    const sorted = [...nutritionChartData].sort(
      (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
    );
    return sorted[0];
  }, [nutritionChartData]);

  const activeGoal =
    client?.nutritionGoals?.find((g) => g.active) ||
    (client?.nutritionGoals?.length ? client.nutritionGoals[0] : null);

  const micro = latest?.micronutrients || {};
  const fiber = typeof micro.fiber === "number" ? micro.fiber : 0;

  let kCal = latest?.kCal;
  if (!(typeof kCal === "number" && kCal > 0) && latest) {
    kCal = Math.round(
      (latest.proteins || 0) * 4 + (latest.carbs || 0) * 4 + (latest.fats || 0) * 9,
    );
  }

  const goalLine = activeGoal
    ? `Goal: ${Math.round(activeGoal.proteins || 0)}P / ${Math.round(activeGoal.carbs || 0)}C / ${Math.round(activeGoal.fats || 0)}F · ${waterLabel(activeGoal.water || 0)} water${activeGoal.fiber ? ` · ${activeGoal.fiber} g fiber` : ""}`
    : null;

  if (!latest) {
    return (
      <Card sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
        <CardHeader title="Nutrition tracking" titleTypographyProps={{ variant: "h6", fontWeight: "bold" }} />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No daily health reports in the last 90 days yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, border: 1, borderColor: "divider", boxShadow: 2 }}>
      <CardHeader
        avatar={<Restaurant color="primary" sx={{ fontSize: 32 }} />}
        title="Nutrition tracking"
        subheader={
          latest.createdOn
            ? `Latest: ${new Date(latest.createdOn).toLocaleDateString()}`
            : null
        }
        titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
      />
      <CardContent sx={{ pt: 0 }}>
        {goalLine ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {goalLine}
          </Typography>
        ) : null}
        <Grid container spacing={2}>
          <Stat
            icon={LocalDrink}
            label="Water"
            value={waterLabel(latest.water || 0)}
            sub={
              activeGoal?.water
                ? `${Math.min(100, Math.round(((latest.water || 0) / activeGoal.water) * 100))}% of goal`
                : null
            }
          />
          <Stat
            icon={Grass}
            label="Fiber"
            value={fiber > 0 ? `${Math.round(fiber * 10) / 10} g` : "—"}
            sub={
              activeGoal?.fiber
                ? `${Math.min(100, Math.round((fiber / activeGoal.fiber) * 100))}% of goal`
                : null
          }
          />
          <Stat
            label="Calories"
            value={`${Math.round(kCal || 0)} kcal`}
            sub={null}
          />
          <Stat
            label="Protein"
            value={`${Math.round(latest.proteins || 0)} g`}
            sub={null}
          />
          <Stat
            label="Carbs"
            value={`${Math.round(latest.carbs || 0)} g`}
            sub={null}
          />
          <Stat
            label="Fats"
            value={`${Math.round(latest.fats || 0)} g`}
            sub={null}
          />
        </Grid>
      </CardContent>
    </Card>
  );
};

export default NutritionTrackingSnapshotCard;
